// API service for bus ticketing system
import { API_BASE_URL } from '../config/api';

// Function to handle logout
const handleLogout = () => {
  // Remove token from localStorage
  if (typeof window !== 'undefined') {
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Redirect to home page
    window.location.href = '/';
  }
};

class BusAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Bus endpoints
  async getBuses(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/buses${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getBusesWithSchedules(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/buses/with-schedules${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getBusById(id, date = null) {
    let endpoint = `/buses/${id}`;
    if (date) {
      endpoint += `?date=${encodeURIComponent(date)}`;
    }
    return this.request(endpoint);
  }

  // Create a new bus
  async createBus(busData) {
    return this.request('/buses', {
      method: 'POST',
      body: JSON.stringify(busData),
    });
  }

  // Update an existing bus
  async updateBus(id, busData) {
    return this.request(`/buses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(busData),
    });
  }

  // Delete a bus
  async deleteBus(id) {
    return this.request(`/buses/${id}`, {
      method: 'DELETE',
    });
  }

  // Upload bus image
  async uploadBusImage(file) {
    const formData = new FormData();
    formData.append('image', file);

    // For file uploads, we don't set Content-Type header, let the browser set it with boundary
    const token = localStorage.getItem('token');
    const response = await fetch(`${this.baseUrl}/buses/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Image upload failed');
    }

    return await response.json();
  }

  // Search endpoint - Fixed to use GET /buses with query parameters
  async searchBuses(searchParams) {
    // Convert searchParams to query string
    const queryParams = new URLSearchParams(searchParams).toString();
    const endpoint = `/buses${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  // Update seat configuration for a bus
  async updateSeatConfiguration(busId, seatConfiguration) {
    return this.request(`/buses/${busId}/seat-configuration`, {
      method: 'PUT',
      body: JSON.stringify({ seatConfiguration }),
    });
  }

  // Get seats for a bus
  async getBusSeats(busId) {
    return this.request(`/seats/bus/${busId}`);
  }

  // Get buses assigned to a specific support user
  async getBusesForSupportUser(userId) {
    return this.request(`/support-user-bus-assignments/buses/${userId}`);
  }

  // Get buses assigned to a specific counter user
  async getBusesForCounter(userId) {
    return this.request(`/bus-counter-assignments/buses/${userId}`);
  }

  // Get buses assigned to the current counter user
  async getCounterAssignedBuses(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = `/buses/assigned${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  // Get buses assigned to the current provider
  async getProviderAssignedBuses(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = `/provider/buses${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  // Inactivity Range Management
  async getBusInactivityRanges(busId) {
    return this.request(`/buses/${busId}/inactivity-ranges`);
  }

  async addBusInactivityRange(rangeData) {
    return this.request('/buses/inactivity-ranges', {
      method: 'POST',
      body: JSON.stringify(rangeData),
    });
  }

  async deleteBusInactivityRange(rangeId) {
    return this.request(`/buses/inactivity-ranges/${rangeId}`, {
      method: 'DELETE',
    });
  }
}

// Seat API Class

class SeatAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Get all seats
  async getAllSeats() {
    return this.request('/seats');
  }

  // Get seat by ID
  async getSeatById(id) {
    return this.request(`/seats/${id}`);
  }

  // Update a seat
  async updateSeat(id, seatData) {
    return this.request(`/seats/${id}`, {
      method: 'PUT',
      body: JSON.stringify(seatData),
    });
  }

  // Bulk update seats
  async bulkUpdateSeats(seatsData) {
    return this.request('/seats/bulk-update', {
      method: 'PUT',
      body: JSON.stringify({ seats: seatsData }),
    });
  }

  // Add new seats to a bus
  async addSeats(busId, seats) {
    return this.request('/seats/add', {
      method: 'POST',
      body: JSON.stringify({ busId, seats }),
    });
  }

  // Delete seats from a bus
  async deleteSeats(busId, seatIds) {
    return this.request('/seats/delete', {
      method: 'DELETE',
      body: JSON.stringify({ busId, seatIds }),
    });
  }

  // Rearrange seat layout
  async rearrangeSeats(busId, seatLayout) {
    return this.request('/seats/rearrange', {
      method: 'PUT',
      body: JSON.stringify({ busId, seatLayout }),
    });
  }

  // Update seat configuration with grid layout (create new seats if needed)
  async updateSeatConfigurationGrid(busId, seatLayout) {
    return this.request('/seats/grid-update', {
      method: 'PUT',
      body: JSON.stringify({ busId, seatLayout }),
    });
  }

  // Get seat layout for a bus
  async getSeatLayout(busId) {
    return this.request(`/seats/layout/${busId}`);
  }

  // Get seat categories
  async getSeatCategories() {
    return this.request('/seat-categories/categories');
  }

  // Get ticket types
  async getTicketTypes() {
    return this.request('/seat-categories/ticket-types');
  }
}

class RouteAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Route endpoints
  async getRoutes() {
    return this.request('/routes');
  }

  // Get routes with pagination and search
  async getRoutesWithPagination(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/routes/paginated${queryParams ? `?${queryParams}` : ''}`);
  }

  async getRouteById(id) {
    return this.request(`/routes/${id}`);
  }

  // Create a new route
  async createRoute(routeData) {
    return this.request('/routes', {
      method: 'POST',
      body: JSON.stringify(routeData),
    });
  }

  // Update an existing route
  async updateRoute(id, routeData) {
    return this.request(`/routes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(routeData),
    });
  }

  // Delete a route
  async deleteRoute(id) {
    return this.request(`/routes/${id}`, {
      method: 'DELETE',
    });
  }
}

class AuthAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Phone number login - request OTP
  async requestOTP(phoneNumber) {
    return this.request('/auth/phone/request-otp', {
      method: 'POST',
      body: JSON.stringify({ phone: phoneNumber }),
    });
  }

  // Verify OTP and login
  async verifyOTP(phoneNumber, otp) {
    return this.request('/auth/phone/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ phone: phoneNumber, otp }),
    });
  }

  // Forgot password - request OTP
  async forgotPassword(phoneNumber) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ phone: phoneNumber }),
    });
  }

  // Reset password with OTP
  async resetPassword(phoneNumber, otp, newPassword) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ phone: phoneNumber, otp, newPassword }),
    });
  }

  // Traditional email/password login
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // Register user
  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Send OTP for registration
  async sendRegistrationOTP(phone) {
    return this.request('/auth/register/send-otp', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  }

  // Google authentication
  async googleLogin() {
    // In a real implementation, this would redirect to the Google OAuth endpoint
    // For now, we'll return a mock response
    window.location.href = `${this.baseUrl}/auth/google`;
  }

  // Facebook authentication
  async facebookLogin() {
    window.location.href = `${this.baseUrl}/auth/facebook`;
  }

  // Get user profile
  async getProfile() {
    return this.request('/auth/profile');
  }

  // Update user profile
  async updateProfile(userData) {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }
}

class ProviderAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Provider bus endpoints
  async getProviderBuses(providerId) {
    return this.request(`/providers/${providerId}/buses`);
  }

  async createProviderBus(providerId, busData) {
    return this.request(`/providers/${providerId}/buses`, {
      method: 'POST',
      body: JSON.stringify(busData),
    });
  }

  async updateProviderBus(providerId, id, busData) {
    return this.request(`/providers/${providerId}/buses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(busData),
    });
  }

  async deleteProviderBus(id) {
    return this.request(`/provider/buses/${id}`, {
      method: 'DELETE',
    });
  }

  // Get all providers for dropdown (without pagination)
  async getAllProvidersDropdown() {
    return this.request('/providers/dropdown');
  }

  // Create a new provider
  async createProvider(providerData) {
    return this.request('/providers', {
      method: 'POST',
      body: JSON.stringify(providerData),
    });
  }

  // Update an existing provider
  async updateProvider(id, providerData) {
    return this.request(`/providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(providerData),
    });
  }

  // Delete a provider
  async deleteProvider(id) {
    return this.request(`/providers/${id}`, {
      method: 'DELETE',
    });
  }

  // Provider coupon management
  async getProviderCoupons(providerId) {
    return this.request(`/providers/${providerId}/coupons`);
  }
  async createProviderCoupon(providerId, couponData) {
    return this.request(`/providers/${providerId}/coupons`, {
      method: 'POST',
      body: JSON.stringify(couponData),
    });
  }

  async updateProviderCoupon(providerId, couponId, couponData) {
    return this.request(`/providers/${providerId}/coupons/${couponId}`, {
      method: 'PUT',
      body: JSON.stringify(couponData),
    });
  }

  async deleteProviderCoupon(providerId, couponId) {
    return this.request(`/providers/${providerId}/coupons/${couponId}`, {
      method: 'DELETE',
    });
  }

  // Provider bookings
  async getProviderBookings(providerId, filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/providers/${providerId}/bookings${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getProviderBookingsWithPagination(providerId, params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/providers/${providerId}/bookings-paginated${queryParams ? `?${queryParams}` : ''}`);
  }

  // Get a specific booking for a provider
  async getProviderBookingById(providerId, bookingId) {
    return this.request(`/providers/${providerId}/bookings/${bookingId}`);
  }

  // Provider statistics
  async getProviderStats(providerId) {
    return this.request(`/providers/${providerId}/stats`);
  }

  // Provider endpoints
  async getProviders(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/providers${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getProviderById(id) {
    return this.request(`/providers/${id}`);
  }

  // Get all counters for a provider
  async getProviderCounters(providerId) {
    return this.request(`/providers/${providerId}/counters`);
  }

  async getProviderSupervisors(providerId) {
    return this.request(`/providers/${providerId}/supervisors`);
  }

}

class BookingAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Booking endpoints
  async getBookings(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getBookingsWithPagination(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/bookings/paginated${queryParams ? `?${queryParams}` : ''}`);
  }

  async getTodaysBookings() {
    return this.request('/bookings/today');
  }

  async getUpcomingBookings() {
    return this.request('/bookings/upcoming');
  }

  async getBookingsWithDateFilter(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/date-filter${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getBookingById(id) {
    return this.request(`/bookings/${id}`);
  }

  async createBooking(bookingData) {
    return this.request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }
  async createBookingWithPassengerDetails(bookingData) {
    return this.request('/bookings/with-passengers', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async createBookingForCustomer(bookingData) {
    return this.request('/bookings/counter-create', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  // New function for support panel bookings
  async createBookingForSupport(bookingData) {
    return this.request('/bookings/support-create', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  // New function for provider panel bookings
  async createBookingForProvider(bookingData) {
    return this.request('/bookings/provider-create', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async createBookingOnHold(bookingData) {
    return this.request('/bookings/on-hold', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  // New function for ticket verification
  async verifyTicketByPNR(pnr) {
    return this.request(`/bookings/verify/${pnr}`);
  }

  async cancelBooking(id) {
    return this.request(`/bookings/${id}/cancel`, {
      method: 'PUT',
    });
  }

  async updateBookingStatus(id, status) {
    return this.request(`/bookings/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteBooking(id) {
    return this.request(`/bookings/${id}`, {
      method: 'DELETE',
    });
  }

  async getDeletedBookings(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/bookings/deleted${query ? '?' + query : ''}`);
  }

  async restoreBooking(id) {
    return this.request(`/bookings/${id}/restore`, {
      method: 'PUT',
    });
  }

  // Coupon validation
  async validateCoupon(couponCode, providerId) {
    return this.request('/bookings/validate-coupon', {
      method: 'POST',
      body: JSON.stringify({ couponCode, providerId }),
    });
  }

  // Ticket verification
  async verifyTicketByPNR(pnr) {
    return this.request(`/bookings/verify/${pnr}`);
  }

  // Reports
  async getPurchaseReport(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/purchase${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getCancellationReport(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/cancellation${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getDailySalesReport(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/daily-sales${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getFinancialReport(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/financial${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  // Disbursement report
  async getDisbursementReport(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/disbursement${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  // Provider reports
  async getProviderDailySalesReport(providerId, filters = {}) {
    const queryParams = new URLSearchParams({ ...filters, providerId }).toString();
    const endpoint = `/bookings/reports/provider/daily-sales${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getProviderDailyIncomeReport(providerId, filters = {}) {
    const queryParams = new URLSearchParams({ ...filters, providerId }).toString();
    const endpoint = `/bookings/reports/provider/daily-income${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getDailyCommissionReport(providerId, filters = {}) {
    const queryParams = new URLSearchParams({ ...filters, providerId }).toString();
    const endpoint = `/bookings/reports/daily-commission${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getProviderFinancialReport(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/provider/financial${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  // Create booking for admin (admin only)
  async createBookingForAdmin(bookingData) {
    return this.request('/bookings/admin-create', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  // Create manual booking for specific user (admin only)
  async createManualBooking(bookingData) {
    return this.request('/bookings/manual', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  // Get provider ticket sales by counter report
  async getProviderTicketSalesByCounter(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/provider/ticket-sales-by-counter${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getProviderTicketSalesBySupervisor(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/bookings/reports/provider/ticket-sales-by-supervisor${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }
}

class PaymentAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

       // For payment endpoints, handle HTML responses (SSLCommerz/Aamarpay redirect forms)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) {
        const html = await response.text();
        return { success: true, isRedirect: true, html };
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Payment endpoints
  async initiatePayment(paymentData) {
    return this.request('/payments/initiate', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async verifyPayment(transactionId) {
    return this.request(`/payments/verify/${transactionId}`);
  }

  async confirmAamarpayPayment(transactionId, bookingId) {
    return this.request(`/payments/aamarpay/success?tran_id=${transactionId}&booking_id=${bookingId}`, {
      method: 'GET',
    });
  }

  async updatePayment(id, paymentData) {
    return this.request(`/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }
}

class UserAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // User endpoints
  async getUsersAll() {
    return this.request('/users/all');
  }

  async getProfile() {
    return this.request('/users/profile');
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async getUserBookings(id) {
    return this.request(`/users/${id}/bookings`);
  }

  async topUpBalance(amount, paymentMethod) {
    return this.request('/users/balance/topup', {
      method: 'PUT',
      body: JSON.stringify({ amount, paymentMethod }),
    });
  }

  async getTopupHistory() {
    return this.request('/users/balance/topup/history');
  }

  async updateProfile(userData) {
    return this.request('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async searchUsersByPhone(phone) {
    const queryParams = new URLSearchParams({ phone }).toString();
    return this.request(`/passenger/search-phone?${queryParams}`);
  }

  async getCustomers() {
    return this.request('/users/customers');
  }

  async getUserById(id) {
    return this.request(`/users/${id}`);
  }

  async updateUser(id, userData) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/users/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async getSupportCustomers() {
    return this.request('/users/support/customers');
  }

  // Upload user avatar
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);

    // Get token from localStorage
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await fetch(`${this.baseUrl}/users/avatar`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async sendSMS(userId, message) {
    return this.request('/users/sms', {
      method: 'POST',
      body: JSON.stringify({ userId, message }),
    });
  }

  // Create user (using users create endpoint)
  async createUser(userData) {
    return this.request('/users/create', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }


  // Get support customers
  async getSupportCustomers() {
    return this.request('/users/support/customers');
  }

  // Send SMS to user
  async sendSMS(userId, message) {
    return this.request(`/users/${userId}/send-sms`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  // Get all users
  async getUsers(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/users${queryParams ? `?${queryParams}` : ''}`);
  }

  // Search users by phone
  // async searchUsersByPhone(phone) {
  //   return this.request(`/users/search?phone=${encodeURIComponent(phone)}`);
  // }

  // Get user by ID
  async getUserById(id) {
    return this.request(`/users/${id}`);
  }

  // Update user
  async updateUser(id, userData) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // Delete user
  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Get user bookings
  async getUserBookings(userId) {
    return this.request(`/users/${userId}/bookings`);
  }

  // Get users by role
  async getUsersByRole(role) {
    return this.request(`/users/by-role?role=${encodeURIComponent(role)}`);
  }

  // Admin change user password
  async adminChangePassword(userId, newPassword) {
    return this.request(`/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ userId, newPassword }),
    });
  }
}
class ReviewAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Review endpoints
  async createReview(reviewData) {
    // Check if we're sending FormData (for file uploads)
    if (reviewData instanceof FormData) {
      return this.request('/customer-reviews', {
        method: 'POST',
        body: reviewData,
        // Remove Content-Type header so browser can set it with proper boundary
        headers: {}
      });
    }

    // For regular JSON data
    return this.request('/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  async getReviewsByProvider(providerId, filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/reviews/provider/${providerId}${queryParams ? `?${queryParams}` : ''}`;
    return this.request(endpoint);
  }

  async getAverageRating(providerId) {
    return this.request(`/reviews/provider/${providerId}/average`);
  }

  async updateReview(id, reviewData) {
    // Check if we're sending FormData (for file uploads)
    if (reviewData instanceof FormData) {
      return this.request(`/customer-reviews/${id}`, {
        method: 'PUT',
        body: reviewData,
        // Remove Content-Type header so browser can set it with proper boundary
        headers: {}
      });
    }

    // For regular JSON data
    return this.request(`/reviews/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reviewData),
    });
  }

  async deleteReview(id) {
    return this.request(`/reviews/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin: Get all reviews
  async getAllReviews(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/customer-reviews/admin/all${queryParams ? `?${queryParams}` : ''}`);
  }
  async getAllReviewsHome(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/customer-reviews/admin/all${queryParams ? `?${queryParams}` : ''}`);
  }
}

class FeedbackAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Feedback endpoints
  async submitFeedback(feedbackData) {
    return this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify(feedbackData),
    });
  }

  async getAllFeedback(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/feedback${queryParams ? `?${queryParams}` : ''}`);
  }

  async getFeedbackById(id) {
    return this.request(`/feedback/${id}`);
  }

  async updateFeedbackStatus(id, status) {
    return this.request(`/feedback/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteFeedback(id) {
    return this.request(`/feedback/${id}`, {
      method: 'DELETE',
    });
  }

  async getFeedbackStats() {
    return this.request('/feedback/stats');
  }

  async getPublicTestimonials(limit = 6) {
    // Make request without authentication to avoid session expiry issues
    const url = `${this.baseUrl}/feedback/testimonials?limit=${limit}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async updateFeedback(id, feedbackData) {
    return this.request(`/feedback/${id}`, {
      method: 'PUT',
      body: JSON.stringify(feedbackData),
    });
  }
}

class OfferAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Public endpoint to get active offers
  async getActiveOffers() {
    return this.request('/offers');
  }

  // Admin endpoints
  async getAllOffers(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/offers/admin${queryParams ? `?${queryParams}` : ''}`);
  }

  async getOfferById(id) {
    return this.request(`/offers/admin/${id}`);
  }

  async createOffer(offerData) {
    return this.request('/offers/admin', {
      method: 'POST',
      body: JSON.stringify(offerData),
    });
  }

  async updateOffer(id, offerData) {
    return this.request(`/offers/admin/${id}`, {
      method: 'PUT',
      body: JSON.stringify(offerData),
    });
  }

  async deleteOffer(id) {
    return this.request(`/offers/admin/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleOfferStatus(id) {
    return this.request(`/offers/admin/${id}/toggle`, {
      method: 'PUT',
    });
  }

  // Upload image for offers
  async uploadOfferImage(file) {
    // Get token from localStorage
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await fetch(`${this.baseUrl}/offers/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: file
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }
}

class SupportTicketAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        // Only logout automatically if skipAutoLogout is not set
        if (!options.skipAutoLogout) {
          handleLogout();
        }
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Support ticket endpoints
  async createSupportTicket(ticketData) {
    return this.request('/support-tickets', {
      method: 'POST',
      body: JSON.stringify(ticketData),
      // Skip automatic logout for support tickets so user can see the error message
      skipAutoLogout: true
    });
  }

  async getMySupportTickets(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/support-tickets/my-tickets${queryParams ? `?${queryParams}` : ''}`);
  }

  async getSupportTicketById(id) {
    return this.request(`/support-tickets/${id}`);
  }

  async getAllSupportTickets(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/support-tickets${queryParams ? `?${queryParams}` : ''}`);
  }

  async updateSupportTicketStatus(id, statusData) {
    return this.request(`/support-tickets/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(statusData),
    });
  }

  async deleteSupportTicket(id) {
    return this.request(`/support-tickets/${id}`, {
      method: 'DELETE',
    });
  }
}

class SystemSettingAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // System setting endpoints
  async getSystemSettings() {
    return this.request('/system-settings');
  }

  async updateSystemSettings(settingsData) {
    return this.request('/system-settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  // Upload image for system settings
  async uploadSystemImage(file) {
    const formData = new FormData();
    formData.append('image', file);

    // Get token from localStorage
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await fetch(`${this.baseUrl}/system-settings/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }
}

class BoardingAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Boarding place endpoints
  async getAllBoardingPlaces(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/boarding-places${queryParams ? `?${queryParams}` : ''}`);
  }

  // Get all boarding places without pagination
  async getAllBoardingPlacesNoPagination(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/boarding-places/all${queryParams ? `?${queryParams}` : ''}`);
  }

  async getBoardingPlaceById(id) {
    return this.request(`/boarding-places/${id}`);
  }

  async createBoardingPlace(boardingPlaceData) {
    return this.request('/boarding-places', {
      method: 'POST',
      body: JSON.stringify(boardingPlaceData),
    });
  }

  async updateBoardingPlace(id, boardingPlaceData) {
    return this.request(`/boarding-places/${id}`, {
      method: 'PUT',
      body: JSON.stringify(boardingPlaceData),
    });
  }

  async deleteBoardingPlace(id) {
    return this.request(`/boarding-places/${id}`, {
      method: 'DELETE',
    });
  }

  async getBoardingPlacesByCity(city, type) {
    const params = new URLSearchParams();
    if (city) params.append('city', city);
    if (type) params.append('type', type);
    return this.request(`/boarding-places/city/search?${params.toString()}`);
  }

  async getBoardingPlacesByRoute(routeId, type) {
    const params = new URLSearchParams();
    if (routeId) params.append('routeId', routeId);
    if (type) params.append('type', type);
    return this.request(`/boarding-places/route/search?${params.toString()}`);
  }
}

class BusBoardingPlaceAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Get all bus boarding place associations
  async getAllBusBoardingPlaces(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/bus-boarding-places${queryParams ? `?${queryParams}` : ''}`);
  }

  // Get boarding places by bus ID
  async getBoardingPlacesByBus(busId) {
    return this.request(`/bus-boarding-places/by-bus/${busId}`);
  }

  // Create a bus boarding place association
  async createBusBoardingPlace(associationData) {
    return this.request('/bus-boarding-places', {
      method: 'POST',
      body: JSON.stringify(associationData),
    });
  }

  // Create multiple bus boarding place associations
  async createMultipleBusBoardingPlaces(busId, associations) {
    console.log('Sending busId:', busId, 'associations:', associations);
    return this.request('/bus-boarding-places/multiple', {
      method: 'POST',
      body: JSON.stringify({ busId, associations }),
    });
  }

  // Update a bus boarding place association
  async updateBusBoardingPlace(id, associationData) {
    return this.request(`/bus-boarding-places/${id}`, {
      method: 'PUT',
      body: JSON.stringify(associationData),
    });
  }

  // Delete a bus boarding place association
  async deleteBusBoardingPlace(id) {
    return this.request(`/bus-boarding-places/${id}`, {
      method: 'DELETE',
    });
  }

  // Delete all associations for a bus
  async deleteAllBusBoardingPlaces(busId) {
    return this.request(`/bus-boarding-places/bus/${busId}`, {
      method: 'DELETE',
    });
  }
}

class BannerAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Banner endpoints
  async getAllBanners() {
    return this.request('/banners/all');
  }

  async getActiveBanners() {
    return this.request('/banners');
  }

  async getBannerById(id) {
    return this.request(`/banners/${id}`);
  }

  async createBanner(bannerData) {
    return this.request('/banners', {
      method: 'POST',
      body: JSON.stringify(bannerData),
    });
  }

  async updateBanner(id, bannerData) {
    return this.request(`/banners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(bannerData),
    });
  }

  async deleteBanner(id) {
    return this.request(`/banners/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleBannerStatus(id) {
    return this.request(`/banners/${id}/toggle`, {
      method: 'PUT',
    });
  }

  // Upload banner image file, returns full public URL
  async uploadBannerImage(file) {
    const formData = new FormData();
    formData.append('image', file);
    const headers = {};
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${this.baseUrl}/system-settings/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to upload image');
    }
    const data = await response.json();
    const imageUrl = data.imageUrl || '';
    if (imageUrl.startsWith('http')) return imageUrl;
    const backendOrigin = this.baseUrl.replace(/\/api$/, '');
    return `${backendOrigin}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  }

  // Create manual booking for specific user (admin only)
  async createManualBooking(bookingData) {
    return this.request('/bookings/manual', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }
}

class EmergencyContactAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Emergency contact endpoints
  async getAllEmergencyContacts() {
    return this.request('/emergency-contacts/all');
  }

  async getActiveEmergencyContacts() {
    return this.request('/emergency-contacts');
  }

  async getEmergencyContactById(id) {
    return this.request(`/emergency-contacts/${id}`);
  }

  async createEmergencyContact(data) {
    return this.request('/emergency-contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmergencyContact(id, data) {
    return this.request(`/emergency-contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEmergencyContact(id) {
    return this.request(`/emergency-contacts/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleEmergencyContactStatus(id) {
    return this.request(`/emergency-contacts/${id}/toggle`, {
      method: 'PUT',
    });
  }
}

// Create API instances
const busAPI = new BusAPI();
const routeAPI = new RouteAPI();
const authAPI = new AuthAPI();
const bookingAPI = new BookingAPI();
const userAPI = new UserAPI();
const paymentAPI = new PaymentAPI();
const providerAPI = new ProviderAPI();
const feedbackAPI = new FeedbackAPI();
const reviewAPI = new ReviewAPI();
const offerAPI = new OfferAPI();
const supportAPI = new SupportTicketAPI();
const systemAPI = new SystemSettingAPI();
const boardingAPI = new BoardingAPI();
const bannerAPI = new BannerAPI();
const emergencyContactAPI = new EmergencyContactAPI();
const busBoardingPlaceAPI = new BusBoardingPlaceAPI();

class ContentSectionAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Get all active content sections
  async getActiveContentSections() {
    return this.request('/content-sections');
  }

  // Get content section by ID
  async getContentSectionById(id) {
    return this.request(`/content-sections/${id}`);
  }

  // Get content section by name
  async getContentSectionByName(name) {
    return this.request(`/content-sections/name/${name}`);
  }

  // Feedback API
  async submitFeedback(feedbackData) {
    return this.request('/feedback', 'POST', feedbackData);
  }

  async getPublicTestimonials(limit = 6) {
    // Make request without authentication to avoid session expiry issues
    const url = `${this.baseUrl}/feedback/testimonials?limit=${limit}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Admin feedback methods
  async getAllFeedback(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/feedback?${queryParams}`);
  }

  async updateFeedback(id, feedbackData) {
    return this.request(`/feedback/${id}`, {
      method: 'PUT',
      body: JSON.stringify(feedbackData),
    });
  }

  async getFeedbackById(id) {
    return this.request(`/feedback/${id}`);
  }

  async updateFeedbackStatus(id, status) {
    return this.request(`/feedback/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteFeedback(id) {
    return this.request(`/feedback/${id}`, {
      method: 'DELETE',
    });
  }

  async getFeedbackStats() {
    return this.request('/feedback/stats');
  }

  // Admin endpoints - Get all content sections (admin only)
  async getAllContentSections() {
    return this.request('/content-sections/all');
  }

  // Admin endpoint - Create content section
  async createContentSection(sectionData) {
    return this.request('/content-sections', {
      method: 'POST',
      body: JSON.stringify(sectionData),
    });
  }

  // Admin endpoint - Update content section
  async updateContentSection(id, sectionData) {
    return this.request(`/content-sections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(sectionData),
    });
  }

  // Admin endpoint - Delete content section
  async deleteContentSection(id) {
    return this.request(`/content-sections/${id}`, {
      method: 'DELETE',
    });
  }
}

const contentSectionAPI = new ContentSectionAPI();

class SeatCategoryAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Seat Category endpoints
  async getSeatCategories() {
    return this.request('/seat-categories/categories');
  }

  async getSeatCategoryById(id) {
    return this.request(`/seat-categories/categories/${id}`);
  }

  async createSeatCategory(categoryData) {
    return this.request('/seat-categories/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateSeatCategory(id, categoryData) {
    return this.request(`/seat-categories/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async deleteSeatCategory(id) {
    return this.request(`/seat-categories/categories/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleSeatCategoryStatus(id) {
    return this.request(`/seat-categories/categories/${id}/toggle`, {
      method: 'PUT',
    });
  }

  // Ticket Type endpoints
  async getTicketTypes() {
    return this.request('/seat-categories/ticket-types');
  }

  async getTicketTypeById(id) {
    return this.request(`/seat-categories/ticket-types/${id}`);
  }

  async createTicketType(typeData) {
    return this.request('/seat-categories/ticket-types', {
      method: 'POST',
      body: JSON.stringify(typeData),
    });
  }

  async updateTicketType(id, typeData) {
    return this.request(`/seat-categories/ticket-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(typeData),
    });
  }

  async deleteTicketType(id) {
    return this.request(`/seat-categories/ticket-types/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleTicketTypeStatus(id) {
    return this.request(`/seat-categories/ticket-types/${id}/toggle`, {
      method: 'PUT',
    });
  }
}

const seatCategoryAPI = new SeatCategoryAPI();
const seatAPI = new SeatAPI();

class SupportUserBusAssignmentAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        // Get error message from response
        handleLogout();
        throw new Error('Invalid credentials');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);

      // Handle network errors specifically
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }

      throw error;
    }
  }

  // Get all support user bus assignments
  async getSupportUserBusAssignments() {
    return this.request('/support-user-bus-assignments');
  }

  // Assign support user to bus
  async assignSupportUserToBus(assignmentData) {
    return this.request('/support-user-bus-assignments', {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
  }

  // Remove support user from bus
  async removeSupportUserFromBus(id) {
    return this.request(`/support-user-bus-assignments/${id}`, {
      method: 'DELETE',
    });
  }

  // Get assignments for a specific support user
  async getSupportUserAssignments(userId) {
    return this.request(`/support-user-bus-assignments/user/${userId}`);
  }

  // Get buses for a specific support user
  async getBusesForSupportUser(userId) {
    return this.request(`/support-user-bus-assignments/buses/${userId}`);
  }
}

const supportUserBusAssignmentAPI = new SupportUserBusAssignmentAPI();

// Bus Counter Assignment API service
class BusCounterAssignmentAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // Helper method to make API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, config);
      // Handle authentication errors
      if (response.status === 401) {
        handleLogout();
        throw new Error('Invalid credentials');
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${error.message}`);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to the server. Please make sure the server is running and try again.');
      }
      throw error;
    }
  }

  // Get all bus-counter assignments
  async getBusCounterAssignments() {
    return this.request('/bus-counter-assignments');
  }

  // Assign counter user to bus
  async assignCounterToBus(assignmentData) {
    return this.request('/bus-counter-assignments', {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
  }

  // Remove counter user from bus
  async removeCounterFromBus(id) {
    return this.request(`/bus-counter-assignments/${id}`, {
      method: 'DELETE',
    });
  }

  // Get assignments for a specific counter user
  async getCounterAssignments(userId) {
    return this.request(`/bus-counter-assignments/user/${userId}`);
  }

  // Get buses for a specific counter user
  async getBusesForCounter(userId) {
    return this.request(`/bus-counter-assignments/buses/${userId}`);
  }
}

const busCounterAssignmentAPI = new BusCounterAssignmentAPI();

class BusScheduleAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Request failed');
    }

    return data;
  }

  async createSchedule(scheduleData) {
    return this.request('/bus-schedules', {
      method: 'POST',
      body: JSON.stringify(scheduleData),
    });
  }

  async getSchedules(busId) {
    const query = busId ? `?busId=${busId}` : '';
    return this.request(`/bus-schedules${query}`);
  }

  async getScheduleById(id) {
    return this.request(`/bus-schedules/${id}`);
  }

  async updateSchedule(id, updateData) {
    return this.request(`/bus-schedules/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateData),
    });
  }

  async deleteSchedule(id) {
    return this.request(`/bus-schedules/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkCreateSchedules(schedules) {
    return this.request('/bus-schedules/bulk', {
      method: 'POST',
      body: JSON.stringify({ schedules }),
    });
  }

  async getSchedulesWithBoardingPlaces(busId) {
    return this.request(`/bus-schedules/with-places?busId=${busId}`);
  }
}

const busScheduleAPI = new BusScheduleAPI();

// class ServiceAPI {
//   constructor() {
//     this.baseUrl = API_BASE_URL;
//   }

//   async getAllServices(category) {
//     const params = category ? `?category=${category}` : '';
//     return this.request(`/services${params}`);
//   }

//   async getActiveServices(category) {
//     const params = category ? `?category=${category}` : '';
//     return this.request(`/services/active${params}`);
//   }

//   async getServiceById(id) {
//     return this.request(`/services/${id}`);
//   }

//   async createService(data) {
//     return this.request('/services', {
//       method: 'POST',
//       body: JSON.stringify(data)
//     });
//   }

//   async updateService(id, data) {
//     return this.request(`/services/${id}`, {
//       method: 'PUT',
//       body: JSON.stringify(data)
//     });
//   }

//   async deleteService(id) {
//     return this.request(`/services/${id}`, {
//       method: 'DELETE'
//     });
//   }
// }

// // Apply the request helper to ServiceAPI
// ServiceAPI.prototype.request = BusAPI.prototype.request;

// const serviceAPI = new ServiceAPI();

class ServiceAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async getAllServices(category) {
    const params = category ? `?category=${category}` : '';
    return this.request(`/services${params}`);
  }

  async getActiveServices(category) {
    const params = category ? `?category=${category}` : '';
    return this.request(`/services/active${params}`);
  }

  async getServiceById(id) {
    return this.request(`/services/${id}`);
  }

  async createService(data) {
    return this.request('/services', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateService(id, data) {
    return this.request(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteService(id) {
    return this.request(`/services/${id}`, {
      method: 'DELETE'
    });
  }
}

// Apply the request helper to ServiceAPI
ServiceAPI.prototype.request = BusAPI.prototype.request;

const serviceAPI = new ServiceAPI();

class OfflineShebaAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async getByServiceType(serviceType) {
    return this.request(`/offline-sheba/by-type?serviceType=${encodeURIComponent(serviceType)}`);
  }

  async getServiceTypes() {
    return this.request('/offline-sheba/types');
  }

  async uploadCSV(serviceType, csvContent) {
    return this.request('/offline-sheba/upload', {
      method: 'POST',
      body: JSON.stringify({ serviceType, csvContent })
    });
  }

  async addRow(serviceType, row) {
    return this.request('/offline-sheba/row', {
      method: 'POST',
      body: JSON.stringify({ serviceType, row })
    });
  }

  async updateRow(id, rowIndex, row) {
    return this.request('/offline-sheba/row', {
      method: 'PUT',
      body: JSON.stringify({ id, rowIndex, row })
    });
  }

  async deleteRecord(id, rowIndex) {
    const params = rowIndex !== undefined ? `?rowIndex=${rowIndex}` : '';
    return this.request(`/offline-sheba/${id}${params}`, {
      method: 'DELETE'
    });
  }
}

OfflineShebaAPI.prototype.request = BusAPI.prototype.request;

const offlineShebaAPI = new OfflineShebaAPI();

class ServiceEnquiryAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async submitEnquiry(data) {
    return this.request('/service-enquiries', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getEnquiries(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/service-enquiries${queryParams ? `?${queryParams}` : ''}`);
  }

  async updateEnquiryStatus(id, status) {
    return this.request(`/service-enquiries/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  async deleteEnquiry(id) {
    return this.request(`/service-enquiries/${id}`, {
      method: 'DELETE'
    });
  }
}

ServiceEnquiryAPI.prototype.request = BusAPI.prototype.request;

const serviceEnquiryAPI = new ServiceEnquiryAPI();

class BuySellListingAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async submitListing(data) {
    return this.request('/buy-sell', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getApprovedListings(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/buy-sell${queryParams ? `?${queryParams}` : ''}`);
  }

  async getAdminListings(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    return this.request(`/buy-sell/admin${queryParams ? `?${queryParams}` : ''}`);
  }

  async updateListingStatus(id, status, adminNote) {
    return this.request(`/buy-sell/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, adminNote })
    });
  }

  async deleteListing(id) {
    return this.request(`/buy-sell/${id}`, {
      method: 'DELETE'
    });
  }
}

BuySellListingAPI.prototype.request = BusAPI.prototype.request;

const buySellListingAPI = new BuySellListingAPI();

export {
  busAPI,
  routeAPI,
  authAPI,
  bookingAPI,
  userAPI,
  paymentAPI,
  providerAPI,
  feedbackAPI,
  reviewAPI,
  offerAPI,
  supportAPI,
  systemAPI,
  boardingAPI,
  bannerAPI,
  contentSectionAPI,
  seatCategoryAPI,
  seatAPI,
  busBoardingPlaceAPI,
  supportUserBusAssignmentAPI,
  busCounterAssignmentAPI,
  busScheduleAPI,
  serviceAPI,
  offlineShebaAPI,
  serviceEnquiryAPI,
  emergencyContactAPI,
  buySellListingAPI,
};

